import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { availableLanguages } from '../utils/translations';
import '../styles/DynamicPanel.css';

// Default navbar configuration
const DEFAULT_NAVBAR_CONFIG = ['bookmarks', 'quick-shopping-list', 'recipes'];

// All available navbar items
const ALL_NAV_ITEMS = [
  { id: 'bookmarks', icon: 'fa-book-atlas', translationKey: 'nav.bookmarks' },
  { id: 'reminders', icon: 'fa-clock-rotate-left', translationKey: 'nav.reminders' },
  { id: 'quick-shopping-list', icon: 'fa-cart-shopping', translationKey: 'nav.quickList' },
  { id: 'quick-todo-list', icon: 'fa-list-check', translationKey: 'nav.todo' },
  { id: 'recipes', icon: 'fa-utensils', translationKey: 'nav.recipes' }
];

const Settings = ({ onClose, onNavbarConfigChange, section = 'all' }) => {
  const { language, switchLanguage, t } = useLanguage();
  const { user, updateEmail, changePassword, deleteAccount } = useAuth();
  const [notifications, setNotifications] = useState(() => {
    try {
      return localStorage.getItem('pushNotificationsEnabled') !== 'false';
    } catch {
      return true;
    }
  });
  const [autoBackup, setAutoBackup] = useState(() => {
    try {
      return localStorage.getItem('autoBackup') !== 'false';
    } catch {
      return true;
    }
  });
  const [autoRemoveInvalidLinks, setAutoRemoveInvalidLinks] = useState(() => {
    return localStorage.getItem('autoRemoveInvalidLinks') === 'true';
  });
  const [autoRemoveInactiveReminders, setAutoRemoveInactiveReminders] = useState(() => {
    return localStorage.getItem('autoRemoveInactiveReminders') === 'true';
  });

  // Navigation Settings
  const [navbarConfig, setNavbarConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('navbarConfig');
      return saved ? JSON.parse(saved) : DEFAULT_NAVBAR_CONFIG;
    } catch {
      return DEFAULT_NAVBAR_CONFIG;
    }
  });

  const [voiceConsent, setVoiceConsent] = useState(() => {
    return localStorage.getItem('voiceConsentGiven') === 'true';
  });

  // Profil: E-Mail-Adresse und/oder Passwort ändern (gemeinsames Formular, ein Button)
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Profil: Konto löschen
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Refs for auto-save on close/unmount
  const autoRemoveInvalidLinksRef = useRef(autoRemoveInvalidLinks);
  const autoRemoveInactiveRemindersRef = useRef(autoRemoveInactiveReminders);
  const navbarConfigRef = useRef(navbarConfig);
  const onNavbarConfigChangeRef = useRef(onNavbarConfigChange);

  // Keep refs in sync with state
  useEffect(() => {
    autoRemoveInvalidLinksRef.current = autoRemoveInvalidLinks;
  }, [autoRemoveInvalidLinks]);

  useEffect(() => {
    autoRemoveInactiveRemindersRef.current = autoRemoveInactiveReminders;
  }, [autoRemoveInactiveReminders]);

  useEffect(() => {
    navbarConfigRef.current = navbarConfig;
  }, [navbarConfig]);

  useEffect(() => {
    onNavbarConfigChangeRef.current = onNavbarConfigChange;
  }, [onNavbarConfigChange]);

  // Auto-save function
  const saveSettings = useCallback(() => {
    try {
      localStorage.setItem('autoRemoveInvalidLinks', autoRemoveInvalidLinksRef.current ? 'true' : 'false');
      localStorage.setItem('autoRemoveInactiveReminders', autoRemoveInactiveRemindersRef.current ? 'true' : 'false');
      localStorage.setItem('navbarConfig', JSON.stringify(navbarConfigRef.current));
      // Notify parent about navbar config change
      if (onNavbarConfigChangeRef.current) {
        onNavbarConfigChangeRef.current(navbarConfigRef.current);
      }
    } catch (e) {
      // noop
    }
  }, []);

  // Auto-save on unmount
  useEffect(() => {
    return () => {
      saveSettings();
    };
  }, [saveSettings]);

  // Handle close with auto-save
  const handleClose = useCallback(() => {
    saveSettings();
    onClose();
  }, [saveSettings, onClose]);

  // Handle navbar item toggle
  const handleNavItemToggle = (itemId) => {
    setNavbarConfig(prev => {
      const isSelected = prev.includes(itemId);

      if (isSelected) {
        // Remove item - but ensure at least 1 remains
        if (prev.length <= 1) {
          return prev; // Can't remove the last item
        }
        return prev.filter(id => id !== itemId);
      } else {
        // Add item - but max 3
        if (prev.length >= 3) {
          return prev; // Can't add more than 3
        }
        return [...prev, itemId];
      }
    });
  };

  // Move item up in the order
  const handleMoveUp = (itemId) => {
    setNavbarConfig(prev => {
      const index = prev.indexOf(itemId);
      if (index <= 0) return prev;
      const newConfig = [...prev];
      [newConfig[index - 1], newConfig[index]] = [newConfig[index], newConfig[index - 1]];
      return newConfig;
    });
  };

  // Move item down in the order
  const handleMoveDown = (itemId) => {
    setNavbarConfig(prev => {
      const index = prev.indexOf(itemId);
      if (index < 0 || index >= prev.length - 1) return prev;
      const newConfig = [...prev];
      [newConfig[index], newConfig[index + 1]] = [newConfig[index + 1], newConfig[index]];
      return newConfig;
    });
  };

  // Profil: E-Mail-Adresse und/oder Passwort ändern - ein gemeinsames Formular, ein Button.
  // Es wird nur geändert, was ausgefüllt wurde; "Aktuelles Passwort" bestätigt die Änderung(en).
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const wantsEmailChange = !!newEmail.trim();
    const wantsPasswordChange = !!(newPassword || confirmNewPassword);

    if (!wantsEmailChange && !wantsPasswordChange) {
      alert(t('settings.nothingToChange') || 'Bitte geben Sie eine neue E-Mail-Adresse und/oder ein neues Passwort ein.');
      return;
    }
    if (!currentPassword) {
      alert(t('settings.passwordRequiredForAction') || 'Bitte geben Sie Ihr Passwort zur Bestätigung ein.');
      return;
    }
    if (wantsPasswordChange) {
      if (newPassword.length < 6) {
        alert(t('passwordTooShort') || 'Passwort muss mindestens 6 Zeichen lang sein');
        return;
      }
      if (newPassword !== confirmNewPassword) {
        alert(t('passwordsDoNotMatch') || 'Passwörter stimmen nicht überein');
        return;
      }
    }

    setSavingProfile(true);
    try {
      const messages = [];
      let hadError = false;

      if (wantsEmailChange) {
        const result = await updateEmail(newEmail, currentPassword);
        if (result.success) {
          messages.push(t('settings.emailChanged') || 'E-Mail-Adresse erfolgreich geändert.');
          setNewEmail('');
        } else {
          hadError = true;
          alert(result.error || t('settings.emailChangeError') || 'Fehler beim Ändern der E-Mail-Adresse.');
        }
      }

      if (wantsPasswordChange) {
        const result = await changePassword(currentPassword, newPassword);
        if (result.success) {
          messages.push(t('settings.passwordChanged') || 'Passwort erfolgreich geändert.');
          setNewPassword('');
          setConfirmNewPassword('');
        } else {
          hadError = true;
          alert(result.error || t('settings.passwordChangeError') || 'Fehler beim Ändern des Passworts.');
        }
      }

      if (messages.length > 0) {
        alert(messages.join('\n'));
      }
      if (!hadError) {
        setCurrentPassword('');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  // Profil: Konto endgültig löschen (inkl. Sicherheitsabfrage)
  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword) {
      alert(t('settings.passwordRequiredForAction') || 'Bitte geben Sie Ihr Passwort zur Bestätigung ein.');
      return;
    }

    if (!window.confirm(
      t('settings.deleteAccountConfirm') ||
      'Möchten Sie Ihr Konto wirklich endgültig löschen? Alle Ihre Daten werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
    )) {
      return;
    }

    setDeletingAccount(true);
    try {
      const result = await deleteAccount(deleteAccountPassword);
      if (result.success) {
        alert(t('settings.accountDeleted') || 'Ihr Konto wurde erfolgreich gelöscht.');
        onClose();
      } else {
        alert(result.error || t('settings.accountDeleteError') || 'Fehler beim Löschen des Kontos.');
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="dynamic-panel">
      <div className="panel-header">
        <h2>
          {section === 'profile'
            ? `👤 ${t('menu.profile') || 'Profil'}`
            : `⚙️ ${t('settings') || 'Einstellungen'}`}
        </h2>
        <button className="close-btn" onClick={handleClose}>✕</button>
      </div>

      <div className="panel-content">
        {/* Sprache - ganz oben */}
        {section === 'all' && (
        <div className="settings-section">
          <h3>🌐 {t('settings.language') || 'Sprache'}</h3>
          <div className="setting-item">
            <label>{t('settings.selectLanguage') || 'Sprache auswählen'}</label>
            <select
              className="language-combobox"
              value={language}
              onChange={(e) => switchLanguage(e.target.value)}
              style={{ marginTop: '8px', padding: '8px', borderRadius: '6px', width: '100%' }}
            >
              {availableLanguages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        )}

        {/* Profil - E-Mail ändern, Passwort ändern, Konto löschen */}
        {section === 'profile' && (
        <div className="settings-section">
          <h3>👤 {t('settings.profile') || 'Profil'}</h3>

          {/* E-Mail-Adresse und/oder Passwort ändern - ein Formular, ein Button */}
          <form onSubmit={handleUpdateProfile} className="setting-item" style={{ marginTop: '8px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              ✉️ {t('settings.currentEmail') || 'E-Mail-Adresse'}: <strong>{user?.email}</strong>
            </label>
            <input
              type="email"
              placeholder={t('settings.newEmail') || 'Neue E-Mail-Adresse'}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', marginBottom: '8px' }}
            />

            <label style={{ display: 'block', marginTop: '12px', marginBottom: '8px' }}>
              🔑 {t('settings.changePasswordBtn') || 'Passwort ändern'}
            </label>
            <input
              type="password"
              placeholder={t('settings.newPassword') || 'Neues Passwort'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', marginBottom: '8px' }}
            />
            <input
              type="password"
              placeholder={t('settings.confirmNewPassword') || 'Neues Passwort bestätigen'}
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px' }}
            />

            <label style={{ display: 'block', marginTop: '12px', marginBottom: '8px' }}>
              🔒 {t('settings.confirmWithPassword') || 'Bestätigen Sie die Änderung mit Ihrem Passwort'}
            </label>
            <input
              type="password"
              placeholder={t('settings.currentPassword') || 'Aktuelles Passwort'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px' }}
              required
            />

            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingProfile}
              style={{ marginTop: '10px', padding: '8px 16px', fontSize: '14px' }}
            >
              {savingProfile ? (t('loading') || 'Lädt...') : (t('settings.applyChange') || 'Ändern')}
            </button>
          </form>

          {/* Konto löschen */}
          <div
            className="setting-item"
            style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(220,53,69,0.3)' }}
          >
            <label style={{ display: 'block', marginBottom: '8px', color: '#dc3545' }}>
              🗑️ {t('settings.deleteAccountTitle') || 'Konto löschen'}
            </label>
            <p className="setting-hint" style={{ color: '#dc3545', marginBottom: '10px', fontSize: '12px' }}>
              {t('settings.deleteAccountWarning') ||
                'Löscht unwiderruflich alle Daten (Lesezeichen, Einkaufslisten, Rezepte, Erinnerungen, geteilte Links).'}
            </p>
            <input
              type="password"
              placeholder={t('settings.deleteAccountPasswordLabel') || 'Mit Passwort bestätigen'}
              value={deleteAccountPassword}
              onChange={(e) => setDeleteAccountPassword(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', marginBottom: '10px' }}
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDeleteAccount}
              disabled={deletingAccount}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                cursor: deletingAccount ? 'not-allowed' : 'pointer',
                opacity: deletingAccount ? 0.6 : 1
              }}
            >
              {deletingAccount
                ? (t('loading') || 'Lädt...')
                : (t('settings.deleteAccountButton') || '🗑️ Endgültig löschen')}
            </button>
          </div>
        </div>
        )}

        {/* Sprachsteuerung - deaktiviert, wird später weiterentwickelt
        <div className="settings-section">
          <h3>🎙️ {t('settings.voiceControl') || 'Sprachsteuerung'}</h3>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={voiceConsent}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setVoiceConsent(checked);
                  if (checked) {
                    localStorage.setItem('voiceConsentGiven', 'true');
                    localStorage.setItem('voiceConsentDate', new Date().toISOString());
                  } else {
                    localStorage.removeItem('voiceConsentGiven');
                    localStorage.removeItem('voiceConsentDate');
                  }
                }}
              />
              {t('settings.voiceActivate') || 'Sprachbefehle aktivieren'}
            </label>
          </div>
          {voiceConsent && (
            <div className="setting-item" style={{ marginTop: '12px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setVoiceConsent(false);
                  localStorage.removeItem('voiceConsentGiven');
                  localStorage.removeItem('voiceConsentDate');
                }}
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                🔐 {t('settings.voicePrivacyRevoke') || 'Sprach-Einwilligung widerrufen'}
              </button>
            </div>
          )}
        </div>
        */}

        {section === 'all' && (
        <>
        <div className="settings-section">
          <h3>{t('settings.notifications') || 'Benachrichtigungen'}</h3>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={notifications}
                onChange={(e) => {
                  const value = e.target.checked;
                  setNotifications(value);
                  try {
                    localStorage.setItem('pushNotificationsEnabled', value ? 'true' : 'false');
                  } catch {
                    // localStorage nicht verfügbar - gilt dann nur für diese Sitzung
                  }
                }}
              />
              {t('settings.pushNotifications') || 'Push-Benachrichtigungen aktivieren'}
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('settings.dataBackup') || 'Datensicherung'}</h3>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={autoBackup}
                onChange={(e) => {
                  const value = e.target.checked;
                  setAutoBackup(value);
                  try {
                    localStorage.setItem('autoBackup', value ? 'true' : 'false');
                  } catch {
                    // localStorage nicht verfügbar - gilt dann nur für diese Sitzung
                  }
                }}
              />
              {t('settings.autoBackup') || 'Automatische Datensicherung'}
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('settings.general') || 'Allgemein'}</h3>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={autoRemoveInvalidLinks}
                onChange={(e) => setAutoRemoveInvalidLinks(e.target.checked)}
              />
              {t('autoRemoveInvalidLinks') || 'Automatisch ungültige Links entfernen'}
            </label>
          </div>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={autoRemoveInactiveReminders}
                onChange={(e) => setAutoRemoveInactiveReminders(e.target.checked)}
              />
              {t('autoRemoveInactiveReminders') || 'Automatisch deaktivierte Reminders entfernen'}
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>🧭 {t('settings.navigation') || 'Navigation'}</h3>
          <p className="section-description">
            {t('settings.navDescription') || 'Wähle bis zu 3 Elemente für die Navigationsleiste.'}
          </p>

          {/* Selected items with order controls */}
          {navbarConfig.length > 0 && (
            <div className="nav-selected-items">
              <strong className="nav-order-label">
                {t('settings.navOrder') || 'Aktive Navigation'} ({navbarConfig.length}/3):
              </strong>
              {navbarConfig.map((itemId, index) => {
                const item = ALL_NAV_ITEMS.find(i => i.id === itemId);
                if (!item) return null;
                return (
                  <div key={itemId} className="nav-order-item">
                    <span className="nav-order-number">
                      {index + 1}.
                    </span>
                    <i className={`fa-solid ${item.icon} nav-order-icon`}></i>
                    <span className="nav-order-text">{t(item.translationKey) || item.id}</span>
                    <button
                      type="button"
                      className="nav-order-btn"
                      onClick={() => handleMoveUp(itemId)}
                      disabled={index === 0}
                      title={t('settings.navMoveUp') || 'Nach oben'}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="nav-order-btn"
                      onClick={() => handleMoveDown(itemId)}
                      disabled={index === navbarConfig.length - 1}
                      title={t('settings.navMoveDown') || 'Nach unten'}
                    >
                      ▼
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* All available items */}
          <div className="nav-available-items">
            {ALL_NAV_ITEMS.map(item => {
              const isSelected = navbarConfig.includes(item.id);
              const canSelect = navbarConfig.length < 3;
              const canDeselect = navbarConfig.length > 1;
              const isDisabled = (isSelected && !canDeselect) || (!isSelected && !canSelect);

              return (
                <div key={item.id} className={`setting-item nav-item-option ${isDisabled ? 'disabled' : ''}`}>
                  <label className="nav-item-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleNavItemToggle(item.id)}
                      disabled={isDisabled}
                      data-testid={`nav-checkbox-${item.id}`}
                    />
                    <i className={`fa-solid ${item.icon} nav-item-icon`}></i>
                    {t(item.translationKey) || item.id}
                  </label>
                </div>
              );
            })}
          </div>

          {navbarConfig.length >= 3 && (
            <p className="setting-hint nav-max-warning">
              ⚠️ {t('settings.navMaxThree') || 'Maximal 3 Elemente können ausgewählt werden'}
            </p>
          )}
        </div>
        </>
        )}

      </div>
    </div>
  );
};

export default Settings;